/**
 * Datadog Error Logging
 *
 * API for error reporting to Datadog.
 * Filters out noise (third-party errors, network issues, browser extensions).
 */

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { buildRequestContext, RequestContext } from './datadogRequestCapture';
import { isErrorLoggedByFetchPatch } from './fetchErrorTracker';

/**
 * Normalize any thrown value into an Error so we consistently get:
 * - message
 * - stack trace (when available)
 *
 * Also returns the original value for safe attachment in Datadog context.
 */
const normalizeThrown = (thrown: unknown): { error: Error; originalError?: unknown } => {
  if (thrown instanceof Error) return { error: thrown };

  if (typeof thrown === 'string') return { error: new Error(thrown), originalError: thrown };

  // Best-effort stringify objects for a useful message without throwing.
  try {
    const str = JSON.stringify(thrown);
    return { error: new Error(str), originalError: thrown };
  } catch {
    return { error: new Error('Unknown error'), originalError: thrown };
  }
};

/**
 * Error messages to ignore (not sent to Datadog)
 */
const IGNORED_ERROR_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications',
  'Request_Cancelled',
  'UnAuthorized',
  'unauthorized_credentials',
  'email_not_found',
  'reset_password',
  'No Internet Connection',
  'Request timed out',
  'Request aborted',
  'SDKAPIUnreachableError',
  'Something went wrong',
  'attempted to hard navigate to the same URL',
  'Network Error',
  'timeout',
  'AbortError',
  'fetch is aborted',
  'StytchApiError',
  'StytchAPIUnreachableError',
  'CSP Violation',
  'Abort fetching component for route',
  'A component is changing from uncontrolled to controlled',
  'A component is changing from controlled to uncontrolled',
  '[Stytch] Unable to retrieve RBAC policy',
  'Ignored attempt to cancel a touchend event',
  'IgnoredEventCancel',
  'Blocked a frame with origin',
  'login link you used is invalid or has expired',
  'session has expired',
  'importing a module script failed',
  'ReferenceError',
  'unexpected end of script',
  'user must have an active stytch session',
  'route did not complete',
  'too many requests have been made',
  // Noise: browser/extension/internal messaging errors that are not actionable for Thera.
  // Seen in Error Tracking as: "Unhandled error response received for message <get-frame-manager-configuration>"
  // Seen in Error Tracking as: "Unhandled error response received for message <shell-plugin-site-config>"
  'Unhandled error response received for message',
  // Browser-level postMessage/iframe origin validation failures (common on Safari iOS / DuckDuckGo Mobile)
  'invalid origin',
  // Valid 401 for users without manager role — not actionable
  'does not have role role_manager on company',
  // Fern SDK 401s — session/permission errors already handled by auth flow
  'Status code: 401',
  // Expected 401s during super-admin impersonation (stale SWR requests with mismatched user ID)
  'Caller user must match',
  'Employees can only view their own contracts',
];

/**
 * Regex patterns for errors to ignore
 */
const IGNORED_ERROR_PATTERNS = [
  /could not be loaded/i,
  /Load failed/i,
  /Failed to load/i,
  /Failed to fetch/i,
  /script error/i,
  // Browsers often report abort errors with name/type="AbortError" and message="signal is aborted without reason"
  /AbortError/i,
  /StytchApiError/i,
  /StytchAPIUnreachableError/i,
  /NetworkError/i,
  /chrome-extension:/i,
  /moz-extension:/i,
  /safari-extension:/i,
  /blocked by.*script-src/i,
  /blocked by.*style-src/i,
  /blocked by.*img-src/i,
  /blocked by.*connect-src/i,
  /blocked by.*font-src/i,
  /blocked by.*frame-src/i,
  /blocked by.*directive/i,
  /refused to load.*violates.*content security policy/i,
  /refused to execute.*violates.*content security policy/i,
  /refused to apply.*violates.*content security policy/i,
  /content security policy.*directive/i,
  /Ignored attempt to cancel a (touch|mouse)end event/i,
  // Noise: generic "Unhandled error response received for message <...>" (often from extensions / internal frame messaging)
  /Unhandled error response received for message <.*?>/i,
];

/**
 * Third-party domains to filter out
 */
const THIRD_PARTY_DOMAINS = ['posthog.com', 'amplitude.com'];

/**
 * Check if error should be filtered out
 */
export const shouldFilterError = (errorMessage: string, errorStack?: string, errorName?: string): boolean => {
  if (!errorMessage || errorMessage === 'undefined' || errorMessage === 'null' || errorMessage === '') {
    return true;
  }

  const msgLower = errorMessage.toLowerCase();
  const stackLower = (errorStack || '').toLowerCase();
  // const nameLower = (errorName || '').toLowerCase();
  // Note: keep this list strictly message-based. Some real app bugs (e.g., JS ReferenceError)
  // have name/type="ReferenceError" but message like "x is not defined" — we don't want to
  // drop those by name.
  if (IGNORED_ERROR_MESSAGES.some((msg) => msgLower.includes(msg.toLowerCase()))) {
    return true;
  }

  // Some patterns represent the error type/name (e.g., /NetworkError/i) which may live in errorName.
  if (
    IGNORED_ERROR_PATTERNS.some(
      (pattern) => pattern.test(errorMessage) || pattern.test(errorStack || '') || pattern.test(errorName || ''),
    )
  ) {
    return true;
  }

  if (THIRD_PARTY_DOMAINS.some((domain) => msgLower.includes(domain) || stackLower.includes(domain))) {
    return true;
  }

  return false;
};

/**
 * Error context for Datadog
 */
export interface ErrorContext {
  /** Where the error originated (e.g., 'TheraToast', 'PaymentModal') */
  source?: string;
  /** Additional context data */
  data?: any;
  /** Is this error shown to the user? Sets user_facing tag */
  isToastError?: boolean;
  /** Error category for classification */
  category?: string;
  /** Custom tags to add */
  tags?: Record<string, string>;
  /**
   * Stable grouping key for Error Tracking. Without it, errors created inside a shared
   * wrapper (e.g. `new Error(message)` in TheraToast) all share one stack signature and
   * collapse into a single issue. Datadog uses `error.dd_fingerprint` ahead of its default
   * stack-based grouping (Browser SDK >= 4.42.0).
   */
  fingerprint?: string;
}

/**
 * Capture an error to Datadog
 *
 * @example
 * // Simple
 * captureError(error);
 *
 * // With context
 * captureError(error, { source: 'PaymentModal', isToastError: true });
 *
 * // With data
 * captureError(error, { source: 'API', data: { userId, endpoint } });
 */
export const captureError = (thrown: unknown, context: ErrorContext = {}): void => {
  const { error, originalError } = normalizeThrown(thrown);
  const errorMessage = error?.message || '';
  const errorStack = error?.stack || '';
  const errorName = error?.name || '';
  const { source = 'App', data: rawData, isToastError = false, category, tags = {}, fingerprint } = context;

  if (!isToastError && shouldFilterError(errorMessage, errorStack, errorName)) {
    console.info('Datadog: Error filtered out (ignored message):', errorMessage);
    return;
  }

  // Skip if already logged by fetch patch (prevents double-logging)
  if (isErrorLoggedByFetchPatch(error)) {
    console.info('Datadog: Error already logged by fetch patch:', errorMessage);
    return;
  }

  // Ensure we always have an object for context merging.
  const data = (rawData && typeof rawData === 'object' ? rawData : rawData ? { data: rawData } : {}) as Record<
    string,
    any
  >;

  // If caller did not provide an original error object, attach the thrown value (when not an Error).
  if (data.originalError === undefined && originalError !== undefined) {
    data.originalError = originalError;
  }

  const errorContext: Record<string, any> = {
    capturedBy: source,
    ...data,
  };

  // Add a couple of high-signal fields for Error Tracking triage/grouping
  try {
    errorContext.error_name = error?.name;
    const errorAny = error as any;
    if (errorAny?.code) errorContext.error_code = errorAny.code;
    if (errorAny?.httpStatus) errorContext.httpStatus = errorAny.httpStatus;
  } catch {
    // no-op
  }

  /**
   * Include request context if present on error (e.g., from `CustomError` in `callApi`).
   *
   * Important: Some callers (notably toast wrappers) may pass a plain object (Axios-like error)
   * and we create a new `Error(...)` for logging. In that case, the `Error` instance itself
   * won't carry request context — so we also attempt to derive it from:
   * - `context.data.requestContext` (if a caller passes it explicitly)
   * - `context.data.originalError` (Axios-like objects can contain `config`/`response`)
   *
   * Safety: `buildRequestContext` already handles redaction + truncation + skipping auth/GETs.
   */
  const requestContext =
    (error as { requestContext?: RequestContext })?.requestContext ||
    (data as { requestContext?: RequestContext })?.requestContext ||
    buildRequestContext((data as { originalError?: unknown })?.originalError);
  if (requestContext) {
    errorContext.api_method = requestContext.method;
    errorContext.api_url = requestContext.url;
    if (requestContext.httpStatus) errorContext.httpStatus = requestContext.httpStatus;
    if (requestContext.requestBody !== undefined) errorContext.api_request_body = requestContext.requestBody;
    if (requestContext.requestBodyTruncated !== undefined) {
      errorContext.api_request_body_truncated = requestContext.requestBodyTruncated;
    }
  }

  if (isToastError) {
    errorContext.thera_error_type = 'toast_error';
    errorContext.user_facing = 'true';
  }

  if (category) {
    errorContext.error_category = category;
  }

  // Set an explicit grouping key so wrapper-created errors don't all collapse into one issue.
  // RUM reads `dd_fingerprint` off the error object; `error.fingerprint` covers log-based grouping.
  if (fingerprint) {
    try {
      (error as { dd_fingerprint?: string }).dd_fingerprint = fingerprint;
    } catch {
      // no-op: error object may be frozen
    }
    errorContext['error.fingerprint'] = fingerprint;
  }

  Object.assign(errorContext, tags);

  // Skip in SSR
  if (typeof window === 'undefined') {
    console.error('[SSR] Datadog error:', errorMessage, errorContext);
    return;
  }

  // Send to Datadog Logs
  try {
    datadogLogs.logger.error(errorMessage, errorContext, error);
  } catch (e) {
    console.error('Failed to log to Datadog:', e);
  }

  // Send to Datadog RUM
  try {
    if (datadogRum?.getInternalContext?.()) {
      datadogRum.addError(error, { ...errorContext, source: 'custom' });
    }
  } catch (e) {
    console.error('Failed to send to Datadog RUM:', e);
  }
};

// Backward compatible aliases
export const captureErrorInDatadog = captureError;
export const logErrorToDatadog = captureError;
